// MSSQL parseable-body fetcher (Slice 17 + ProceduralBodyIndexing Slice 1.1).
// Parseable kinds: V/IF/FN modules, default + check constraints (existing),
// P procedures + TR triggers (procedural feed). Triggers inherit their schema
// from the parent table via `sys.triggers.parent_id`.

import type {
  ObjectKind,
  PersistedObjectListing,
  QualifiedName,
} from '../../types';

import {
  asString,
  escapeSqlLiteral,
  fetchListingRows,
  fetchRows,
  makeListing,
  type NavListingFetchOptions,
} from './queryHelper';

function listing(
  objectKind: PersistedObjectListing['objectKind'],
  schema: string,
  name: string,
  bodyText: string,
): PersistedObjectListing {
  return makeListing('mssql', objectKind, schema, name, bodyText);
}

// Page key: `sys.sql_modules.object_id` is the table's PK, so it is a unique
// total order — the invariant OFFSET paging needs.
const MODULES_SQL = `SELECT s.name AS [schema], o.name AS name, o.type AS object_type, m.definition AS body
FROM sys.sql_modules m
JOIN sys.objects o ON o.object_id = m.object_id
JOIN sys.schemas s ON s.schema_id = o.schema_id
WHERE o.type IN ('V', 'IF', 'FN')`;

const DEFAULTS_SQL = `SELECT s.name AS [schema], dc.name AS name, dc.definition AS body
FROM sys.default_constraints dc
JOIN sys.objects o ON o.object_id = dc.parent_object_id
JOIN sys.schemas s ON s.schema_id = o.schema_id`;

const CHECKS_SQL = `SELECT s.name AS [schema], cc.name AS name, cc.definition AS body
FROM sys.check_constraints cc
JOIN sys.objects o ON o.object_id = cc.parent_object_id
JOIN sys.schemas s ON s.schema_id = o.schema_id`;

function moduleKind(type: string): PersistedObjectListing['objectKind'] | null {
  const trimmed = type.trim();
  if (trimmed === 'V') return 'view';
  if (trimmed === 'IF') return 'inline-tvf';
  if (trimmed === 'FN') return 'scalar-function';
  return null;
}

export async function listMssqlParseableObjects(
  options: NavListingFetchOptions,
): Promise<readonly PersistedObjectListing[]> {
  const [moduleRows, defaultRows, checkRows] = await Promise.all([
    fetchListingRows(options, {
      engine: 'mssql',
      label: 'mssql-modules',
      sql: MODULES_SQL,
      orderBy: 'm.object_id',
      dialect: 'offset-fetch',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'mssql',
      label: 'mssql-defaults',
      sql: DEFAULTS_SQL,
      orderBy: 'dc.object_id',
      dialect: 'offset-fetch',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'mssql',
      label: 'mssql-checks',
      sql: CHECKS_SQL,
      orderBy: 'cc.object_id',
      dialect: 'offset-fetch',
      bodyColumns: ['body'],
    }),
  ]);

  const out: PersistedObjectListing[] = [];

  for (const row of moduleRows) {
    const kind = moduleKind(asString(row.object_type));
    const body = asString(row.body);
    const name = asString(row.name);
    if (!kind || !name || !body) continue;
    out.push(listing(kind, asString(row.schema), name, body));
  }

  for (const row of defaultRows) {
    const name = asString(row.name);
    const body = asString(row.body);
    if (!name || !body) continue;
    out.push(listing('default-constraint', asString(row.schema), name, body));
  }

  for (const row of checkRows) {
    const name = asString(row.name);
    const body = asString(row.body);
    if (!name || !body) continue;
    out.push(listing('check-constraint', asString(row.schema), name, body));
  }

  return out;
}

/** Schemas holding an object named `name` of any of `kinds` — the DDL
 *  invalidator's "where did this bare statement run?" probe. */
export async function listMssqlSchemasHoldingObject(
  connectionId: string,
  database: string,
  name: string,
  kinds: readonly ObjectKind[],
): Promise<readonly string[]> {
  const types = kinds.flatMap((kind) => MSSQL_TYPE_BY_KIND[kind] ?? []);
  if (types.length === 0) return [];
  const sql = `SELECT DISTINCT SCHEMA_NAME(o.schema_id) AS [schema] FROM sys.objects o
    WHERE o.name = '${escapeSqlLiteral(name)}' AND o.type IN (${types.map((t) => `'${t}'`).join(', ')})`;
  const rows = await fetchRows(connectionId, database, sql, 'mssql-schemas-of');
  return rows.map((r) => asString(r.schema)).filter((s) => s.length > 0);
}

const MSSQL_TYPE_BY_KIND: Partial<Record<ObjectKind, readonly string[]>> = {
  view: ['V'],
  'inline-tvf': ['IF'],
  'scalar-function': ['FN'],
  procedure: ['P'],
  trigger: ['TR'],
};

export async function fetchMssqlObjectBody(
  connectionId: string,
  database: string,
  objectKind: ObjectKind,
  objectName: QualifiedName,
): Promise<string | null> {
  const schema = objectName.schema ?? 'dbo';
  const s = escapeSqlLiteral(schema);
  const n = escapeSqlLiteral(objectName.name);

  if (objectKind === 'view' || objectKind === 'inline-tvf' || objectKind === 'scalar-function') {
    const typeFilter =
      objectKind === 'view' ? `('V')` : objectKind === 'inline-tvf' ? `('IF')` : `('FN')`;
    const sql = `SELECT m.definition AS body FROM sys.sql_modules m
      JOIN sys.objects o ON o.object_id = m.object_id
      JOIN sys.schemas s ON s.schema_id = o.schema_id
      WHERE s.name = '${s}' AND o.name = '${n}' AND o.type IN ${typeFilter}`;
    const rows = await fetchRows(connectionId, database, sql, 'mssql-body');
    const body = asString(rows[0]?.body);
    return body || null;
  }

  if (objectKind === 'default-constraint') {
    const sql = `SELECT dc.definition AS body FROM sys.default_constraints dc
      JOIN sys.objects o ON o.object_id = dc.parent_object_id
      JOIN sys.schemas s ON s.schema_id = o.schema_id
      WHERE s.name = '${s}' AND dc.name = '${n}'`;
    const rows = await fetchRows(connectionId, database, sql, 'mssql-default');
    const body = asString(rows[0]?.body);
    return body || null;
  }

  if (objectKind === 'check-constraint') {
    const sql = `SELECT cc.definition AS body FROM sys.check_constraints cc
      JOIN sys.objects o ON o.object_id = cc.parent_object_id
      JOIN sys.schemas s ON s.schema_id = o.schema_id
      WHERE s.name = '${s}' AND cc.name = '${n}'`;
    const rows = await fetchRows(connectionId, database, sql, 'mssql-check');
    const body = asString(rows[0]?.body);
    return body || null;
  }

  return null;
}

const PROC_SQL = `SELECT s.name AS [schema], o.name AS name, m.definition AS body
FROM sys.sql_modules m
JOIN sys.objects o ON o.object_id = m.object_id
JOIN sys.schemas s ON s.schema_id = o.schema_id
WHERE o.type = 'P' AND o.is_ms_shipped = 0 AND s.name NOT IN ('sys', 'INFORMATION_SCHEMA')`;

const TRIGGER_SQL = `SELECT s.name AS [schema], t.name AS name, m.definition AS body
FROM sys.sql_modules m
JOIN sys.triggers t ON t.object_id = m.object_id
JOIN sys.objects p ON p.object_id = t.parent_id
JOIN sys.schemas s ON s.schema_id = p.schema_id
WHERE t.is_ms_shipped = 0 AND s.name NOT IN ('sys', 'INFORMATION_SCHEMA')`;

export async function listMssqlProceduralObjects(
  options: NavListingFetchOptions,
): Promise<readonly PersistedObjectListing[]> {
  const [procRows, triggerRows] = await Promise.all([
    fetchListingRows(options, {
      engine: 'mssql',
      label: 'mssql-procedures',
      sql: PROC_SQL,
      orderBy: 'm.object_id',
      dialect: 'offset-fetch',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'mssql',
      label: 'mssql-triggers',
      sql: TRIGGER_SQL,
      orderBy: 't.object_id',
      dialect: 'offset-fetch',
      bodyColumns: ['body'],
    }),
  ]);

  const out: PersistedObjectListing[] = [];
  for (const row of procRows) {
    const name = asString(row.name);
    const body = asString(row.body);
    if (!name || !body) continue;
    out.push(listing('procedure', asString(row.schema), name, body));
  }
  for (const row of triggerRows) {
    const name = asString(row.name);
    const body = asString(row.body);
    if (!name || !body) continue;
    out.push(listing('trigger', asString(row.schema), name, body));
  }
  return out;
}

export async function fetchMssqlProceduralObjectBody(
  connectionId: string,
  database: string,
  objectKind: ObjectKind,
  objectName: QualifiedName,
): Promise<string | null> {
  const s = escapeSqlLiteral(objectName.schema ?? 'dbo');
  const n = escapeSqlLiteral(objectName.name);

  if (objectKind === 'procedure') {
    const sql = `SELECT m.definition AS body FROM sys.sql_modules m
      JOIN sys.objects o ON o.object_id = m.object_id
      JOIN sys.schemas s ON s.schema_id = o.schema_id
      WHERE s.name = '${s}' AND o.name = '${n}' AND o.type = 'P'`;
    const rows = await fetchRows(connectionId, database, sql, 'mssql-procedure-body');
    return asString(rows[0]?.body) || null;
  }

  if (objectKind === 'trigger') {
    const sql = `SELECT m.definition AS body FROM sys.sql_modules m
      JOIN sys.triggers t ON t.object_id = m.object_id
      JOIN sys.objects p ON p.object_id = t.parent_id
      JOIN sys.schemas s ON s.schema_id = p.schema_id
      WHERE s.name = '${s}' AND t.name = '${n}'`;
    const rows = await fetchRows(connectionId, database, sql, 'mssql-trigger-body');
    return asString(rows[0]?.body) || null;
  }

  return null;
}
